/** Shared, framework-free progress schema used by the browser and Pages API. */
export type ProgressField = 'read' | 'bookmarked' | 'notes' | 'confidence' | 'challengeDone';
export interface AnswerProgress {
  correct: boolean;
  attempts: number;
  updatedAt: number;
  counters?: Record<string, number>;
}
export interface NoteVersion { text: string; updatedAt: number }
export interface ChapterProgress {
  read?: boolean;
  bookmarked?: boolean;
  notes?: string;
  confidence?: number;
  challengeDone?: boolean;
  answers?: Record<string, AnswerProgress>;
  updatedAt: number;
  fieldTimes?: Partial<Record<ProgressField, number>>;
  noteHistory?: NoteVersion[];
  noteActor?: string;
}
export interface StudyProgress {
  schemaVersion: 1;
  chapters: Record<string, ChapterProgress>;
  lastChapter?: string;
  lastChapterAt?: number;
  updatedAt: number;
}

export const MAX_PROGRESS_BYTES = 512_000;
export const MAX_NOTES_LENGTH = 12_000;
export const STUDY_KEY_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,79}$/;
const FIELDS: ProgressField[] = ['read', 'bookmarked', 'notes', 'confidence', 'challengeDone'];
const FORBIDDEN_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
export const emptyProgress = (): StudyProgress => ({ schemaVersion: 1, chapters: {}, updatedAt: 0 });

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
function requireValid(condition: unknown, message: string): asserts condition {
  if (!condition) throw new Error(message);
}
function timestamp(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value <= Date.now() + 86_400_000;
}
export function validId(value: unknown): value is string {
  return typeof value === 'string' && ID_PATTERN.test(value) && !FORBIDDEN_KEYS.has(value);
}

/** Rebuild rather than spread untrusted objects: no unknown properties or prototype keys survive. */
export function validateProgress(value: unknown): StudyProgress {
  requireValid(record(value) && value.schemaVersion === 1, 'This is not a supported LearnCFD progress file.');
  requireValid(record(value.chapters) && Object.keys(value.chapters).length <= 128, 'Invalid chapter progress.');
  requireValid(timestamp(value.updatedAt), 'Invalid progress timestamp.');
  const result: StudyProgress = { schemaVersion: 1, chapters: {}, updatedAt: value.updatedAt };
  if (value.lastChapter !== undefined) {
    requireValid(validId(value.lastChapter), 'Invalid last chapter.');
    result.lastChapter = value.lastChapter;
  }
  if (value.lastChapterAt !== undefined) {
    requireValid(timestamp(value.lastChapterAt), 'Invalid chapter timestamp.');
    result.lastChapterAt = value.lastChapterAt;
  }
  for (const [id, chapter] of Object.entries(value.chapters)) {
    requireValid(validId(id) && record(chapter) && timestamp(chapter.updatedAt), 'Invalid chapter record.');
    const parsed: ChapterProgress = { updatedAt: chapter.updatedAt };
    for (const field of ['read', 'bookmarked', 'challengeDone'] as const) {
      if (chapter[field] !== undefined) {
        requireValid(typeof chapter[field] === 'boolean', `Invalid ${field} value.`);
        parsed[field] = chapter[field];
      }
    }
    if (chapter.notes !== undefined) {
      requireValid(typeof chapter.notes === 'string' && chapter.notes.length <= MAX_NOTES_LENGTH, 'Notes exceed the 12,000 character limit.');
      parsed.notes = chapter.notes;
    }
    if (chapter.confidence !== undefined) {
      requireValid(typeof chapter.confidence === 'number' && Number.isInteger(chapter.confidence) && chapter.confidence >= 0 && chapter.confidence <= 5, 'Confidence must be between 0 and 5.');
      parsed.confidence = chapter.confidence;
    }
    if (chapter.fieldTimes !== undefined) {
      requireValid(record(chapter.fieldTimes), 'Invalid field timestamps.');
      parsed.fieldTimes = {};
      for (const field of FIELDS) {
        if (chapter.fieldTimes[field] !== undefined) {
          requireValid(timestamp(chapter.fieldTimes[field]), 'Invalid field timestamp.');
          parsed.fieldTimes[field] = chapter.fieldTimes[field];
        }
      }
    }
    if (chapter.noteHistory !== undefined) {
      requireValid(Array.isArray(chapter.noteHistory) && chapter.noteHistory.length <= 30, 'Invalid note history.');
      parsed.noteHistory = chapter.noteHistory.map((note: unknown) => {
        requireValid(record(note) && typeof note.text === 'string' && note.text.length <= MAX_NOTES_LENGTH && timestamp(note.updatedAt), 'Invalid saved note.');
        return { text: note.text, updatedAt: note.updatedAt };
      });
    }
    if (chapter.noteActor !== undefined) {
      requireValid(validId(chapter.noteActor), 'Invalid note author identifier.');
      parsed.noteActor = chapter.noteActor;
    }
    if (chapter.answers !== undefined) {
      requireValid(record(chapter.answers) && Object.keys(chapter.answers).length <= 128, 'Invalid answers.');
      parsed.answers = {};
      for (const [questionId, answer] of Object.entries(chapter.answers)) {
        requireValid(validId(questionId) && record(answer) && typeof answer.correct === 'boolean' && timestamp(answer.updatedAt), 'Invalid answer record.');
        requireValid(Number.isSafeInteger(answer.attempts) && Number(answer.attempts) >= 1 && Number(answer.attempts) <= 1_000_000, 'Invalid attempt count.');
        const item: AnswerProgress = { correct: answer.correct, attempts: Number(answer.attempts), updatedAt: answer.updatedAt };
        if (answer.counters !== undefined) {
          requireValid(record(answer.counters) && Object.keys(answer.counters).length <= 256, 'Invalid answer counters.');
          item.counters = {};
          for (const [actor, count] of Object.entries(answer.counters)) {
            requireValid(validId(actor) && Number.isSafeInteger(count) && Number(count) >= 1 && Number(count) <= 1_000_000, 'Invalid answer counter.');
            item.counters[actor] = Number(count);
          }
          const count = Object.values(item.counters).reduce((sum, entry) => sum + entry, 0);
          requireValid(count === item.attempts, 'Answer counters do not match attempts.');
        }
        parsed.answers[questionId] = item;
      }
    }
    result.chapters[id] = parsed;
  }
  const compacted = compactNoteHistory(result);
  requireValid(new TextEncoder().encode(JSON.stringify(compacted)).length <= MAX_PROGRESS_BYTES, 'Current notes and answers exceed the 500 KB storage limit. Export a backup and shorten older notes.');
  return compacted;
}

/** Current notes/answers take priority. Evict the oldest optional snapshots across all
 * chapters until the serialized record fits; never evict current learning progress. */
export function compactNoteHistory(progress: StudyProgress): StudyProgress {
  const encoder = new TextEncoder();
  let bytes = encoder.encode(JSON.stringify(progress)).length;
  if (bytes <= MAX_PROGRESS_BYTES) return progress;
  const result: StudyProgress = { ...progress, chapters: Object.fromEntries(Object.entries(progress.chapters).map(([id, chapter]) => [id, { ...chapter, ...(chapter.noteHistory ? { noteHistory: [...chapter.noteHistory] } : {}) }])) };
  for (const chapter of Object.values(result.chapters)) if (!chapter.noteHistory?.length) delete chapter.noteHistory;
  bytes = encoder.encode(JSON.stringify(result)).length;
  const candidates = Object.entries(result.chapters).flatMap(([id, chapter]) => (chapter.noteHistory ?? []).map(note => ({ id, note })))
    .sort((a, b) => a.note.updatedAt - b.note.updatedAt || a.id.localeCompare(b.id) || a.note.text.localeCompare(b.note.text));
  for (const { id, note } of candidates) {
    if (bytes <= MAX_PROGRESS_BYTES) break;
    const chapter = result.chapters[id];
    const history = chapter.noteHistory!;
    // JSON member overhead is constant, regardless of its position in the object.
    bytes -= encoder.encode(JSON.stringify(note)).length + (history.length === 1 ? ',"noteHistory":[]'.length : 1);
    history.splice(history.indexOf(note), 1);
    if (!history.length) delete chapter.noteHistory;
  }
  return result;
}

export function parseProgress(text: string): StudyProgress {
  requireValid(new TextEncoder().encode(text).length <= MAX_PROGRESS_BYTES, 'Progress file is too large (maximum 500 KB).');
  try { return validateProgress(JSON.parse(text)); }
  catch (error) {
    if (error instanceof SyntaxError) throw new Error('The file is not valid JSON.');
    throw error;
  }
}

export function notesHistory(...chapters: (ChapterProgress | undefined)[]): NoteVersion[] {
  const entries = new Map<string, NoteVersion>();
  for (const chapter of chapters) {
    if (!chapter) continue;
    for (const note of [...(chapter.noteHistory ?? []), ...(chapter.notes !== undefined ? [{ text: chapter.notes, updatedAt: chapter.fieldTimes?.notes ?? chapter.updatedAt }] : [])]) {
      const previous = entries.get(note.text);
      if (!previous || previous.updatedAt < note.updatedAt) entries.set(note.text, note);
    }
  }
  return [...entries.values()].sort((a, b) => b.updatedAt - a.updatedAt || a.text.localeCompare(b.text)).slice(0, 30);
}

function choose<T>(a: T, aTime: number, b: T, bTime: number): T {
  if (aTime !== bTime) return aTime > bTime ? a : b;
  return JSON.stringify(a) >= JSON.stringify(b) ? a : b;
}

/** Field-wise last-write-wins, with a grow-only per-session counter for quiz attempts.
 * false/0/empty strings are explicit values, so clearing a field survives synchronization.
 * The last 30 distinct note versions remain recoverable, including competing device edits.
 */
export function mergeProgress(a: StudyProgress, b: StudyProgress): StudyProgress {
  const result = emptyProgress();
  result.updatedAt = Math.max(a.updatedAt, b.updatedAt);
  if (a.lastChapter || b.lastChapter) {
    const left = a.lastChapterAt ?? a.updatedAt;
    const right = b.lastChapterAt ?? b.updatedAt;
    result.lastChapter = !a.lastChapter ? b.lastChapter : !b.lastChapter ? a.lastChapter : choose(a.lastChapter, left, b.lastChapter, right);
    result.lastChapterAt = !a.lastChapter ? right : !b.lastChapter ? left : Math.max(left, right);
  }
  for (const id of [...new Set([...Object.keys(a.chapters), ...Object.keys(b.chapters)])].sort()) {
    const left = a.chapters[id];
    const right = b.chapters[id];
    const chapter: ChapterProgress = { updatedAt: Math.max(left?.updatedAt ?? 0, right?.updatedAt ?? 0), fieldTimes: {} };
    for (const field of FIELDS) {
      const lv = left?.[field];
      const rv = right?.[field];
      if (lv === undefined && rv === undefined) continue;
      const lt = lv === undefined ? 0 : left?.fieldTimes?.[field] ?? left?.updatedAt ?? 0;
      const rt = rv === undefined ? 0 : right?.fieldTimes?.[field] ?? right?.updatedAt ?? 0;
      const value = lv === undefined ? rv : rv === undefined ? lv : choose(lv, lt, rv, rt);
      Object.assign(chapter, { [field]: value });
      chapter.fieldTimes![field] = Math.max(lt, rt);
    }
    if (chapter.notes !== undefined) {
      const source = [left, right].filter((entry): entry is ChapterProgress => !!entry && entry.notes === chapter.notes)
        .sort((x, y) => (y.fieldTimes?.notes ?? y.updatedAt) - (x.fieldTimes?.notes ?? x.updatedAt) || (y.noteActor ?? '').localeCompare(x.noteActor ?? ''))[0];
      if (source?.noteActor) chapter.noteActor = source.noteActor;
    }
    // A cloud echo of this same editor is not a conflicting note. Sample it at
    // 30-second intervals; independently edited device versions remain recoverable.
    let historyLeft = left;
    let historyRight = right;
    if (left?.noteActor && left.noteActor === right?.noteActor) {
      const delta = (left.fieldTimes?.notes ?? left.updatedAt) - (right.fieldTimes?.notes ?? right.updatedAt);
      if (delta > 0 && delta < 30_000) historyRight = { ...right, notes: undefined };
      if (delta < 0 && delta > -30_000) historyLeft = { ...left, notes: undefined };
    }
    const history = notesHistory(historyLeft, historyRight).filter(note => note.text !== chapter.notes);
    if (history.length) chapter.noteHistory = history;
    const questionIds = [...new Set([...Object.keys(left?.answers ?? {}), ...Object.keys(right?.answers ?? {})])].sort();
    if (questionIds.length) chapter.answers = {};
    for (const questionId of questionIds) {
      const la = left?.answers?.[questionId];
      const ra = right?.answers?.[questionId];
      const latest = !la ? ra! : !ra ? la : choose(la, la.updatedAt, ra, ra.updatedAt);
      const counters: Record<string, number> = {};
      for (const answer of [la, ra]) {
        if (!answer) continue;
        for (const [actor, count] of Object.entries(answer.counters ?? { legacy: answer.attempts })) counters[actor] = Math.max(counters[actor] ?? 0, count);
      }
      chapter.answers![questionId] = { correct: latest.correct, attempts: Object.values(counters).reduce((sum, count) => sum + count, 0), updatedAt: latest.updatedAt, counters: Object.fromEntries(Object.entries(counters).sort()) };
    }
    result.chapters[id] = chapter;
  }
  return result;
}

export function sameProgress(a: StudyProgress, b: StudyProgress): boolean {
  return JSON.stringify(mergeProgress(a, emptyProgress())) === JSON.stringify(mergeProgress(b, emptyProgress()));
}
